import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { AsyncPipe, isPlatformBrowser, LowerCasePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Signal,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatBadge } from '@angular/material/badge';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import {
  ButtonComponent,
  IconButtonComponent,
  IconButtonIconFontSize,
  IconButtonIconSize,
  IconComponent,
} from '@claas/claas-ui-components';
import { Store } from '@ngrx/store';
import {
  InterpolatableTranslationObject,
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import {
  IOrganisationListItem,
  IOrganisationNotificationCountItem,
  IOrganisationRegistrationDialogInput,
  IRegistrationDialogStep,
  User,
} from 'cde-fe-organization-registration-dialog';
import { combineLatest, Observable, Subscription, take, tap } from 'rxjs';
import { NotificationsPanelComponent } from '@cde-fe-app/app/features/notifications/views/notifications-panel/notifications-panel.component';
import { MiniCartComponent } from '@cde-fe-app/app/features/parts-shop/views/mini-cart/mini-cart.component';
import { FeatureToggleDirective } from '@cde-fe-app/app/shared/directive/featureToggle.directive';
import { HasPermissionsDirective } from '@cde-fe-app/app/shared/directive/hasPermissions.directive';
import { IEnvironment } from '@cde-fe-app/app/shared/models/environment.model';
import { CustomDimension } from '@cde-fe-app/app/shared/models/event-tracking.model';
import { Features } from '@cde-fe-app/app/shared/models/feature-toggle.model';
import { INotification } from '@cde-fe-app/app/shared/models/notification.model';
import { EventTrackingService } from '@cde-fe-app/app/shared/services/event-tracking.service';
import { NotificationsService } from '@cde-fe-app/app/shared/services/notifications.service';
import * as RootState from '@cde-fe-app/app/shared/state';
import * as AuthState from '@cde-fe-app/app/shared/state/auth';
import * as AuthActions from '@cde-fe-app/app/shared/state/auth/auth.actions';
import { getOrganisationChanging } from '@cde-fe-app/app/shared/state/organisations';
import * as OrganisationsState from '@cde-fe-app/app/shared/state/organisations';
import * as OrganisationActions from '@cde-fe-app/app/shared/state/organisations/organisations.actions';
import { OrgRegistrationDialogConsumerBase } from '@cde-fe-app/app/shared/utils/org-registration-dialog-consumer-base';
import { ORGANIZATION_MANAGE_PERMISSION, PLATTFORM_ADVANCED_PERMISSION } from '@cde-fe-app/app/shared/utils/permission';
import { environment } from '@cde-fe-app/environments/environment';
import { AppLauncherComponent } from './app-launcher/app-launcher.component';
import { HelpMenuComponent } from './help-menu/help-menu.component';
import { LanguagesComponent } from './languages/languages.component';

type ExtendedUser = User & { picture: string };

interface LegalTextLink {
  link: string;
  label: string;
}

@Component({
  selector: 'app-right-navbar',
  templateUrl: './right-navbar.component.html',
  styleUrls: ['./right-navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AppLauncherComponent,
    AsyncPipe,
    ButtonComponent,
    FeatureToggleDirective,
    HasPermissionsDirective,
    HelpMenuComponent,
    IconButtonComponent,
    LanguagesComponent,
    LowerCasePipe,
    MatBadge,
    MatButton,
    MatDivider,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MiniCartComponent,
    NgClass,
    NgTemplateOutlet,
    NotificationsPanelComponent,
    TranslateModule,
    IconComponent,
  ],
})
export class RightNavbarComponent
  extends OrgRegistrationDialogConsumerBase
  implements OnInit, OnDestroy, AfterViewInit
{
  // Public injected services
  readonly dialog = inject(MatDialog);
  readonly cdRef = inject(ChangeDetectorRef);
  readonly breakpointObserver = inject(BreakpointObserver);
  readonly translateService = inject(TranslateService);
  readonly store = inject<Store<RootState.IState>>(Store<RootState.IState>);

  // Public properties
  public env: IEnvironment = environment;
  public user$ = this.store.select(AuthState.userSelector);
  public showNavbar = signal(false);
  public stage = environment.stage;
  public platformAdvanced = PLATTFORM_ADVANCED_PERMISSION;
  public manageOrgPermission = ORGANIZATION_MANAGE_PERMISSION;
  public features = Features;
  public smallIconButton = IconButtonIconSize.SMALL;
  public smallIconFont = IconButtonIconFontSize.SMALL;
  public currentUser = signal<ExtendedUser | null>(null);
  public organisations = signal<IOrganisationListItem[]>([]);
  public allOrgNotificationCount$: Observable<IOrganisationNotificationCountItem[]> = this.store.select(
    OrganisationsState.getOrganisationNotificationsCount
  );
  public allOrgNotificationCount = signal<IOrganisationNotificationCountItem[]>([]);
  public organisations$ = this.store
    .select(OrganisationsState.getUserOrganisations)
    .pipe(tap(organisations => this.hasNonDemoOrg.set(organisations.some(org => org.registered))));
  public lastSelectedOrganisation$ = this.store.select(OrganisationsState.getLastSelectedOrganisationItem);
  public registrationDialogSteps$ = this.store.select(OrganisationsState.getRegistrationDialogSteps);
  public currentOrg = toSignal(this.store.select(OrganisationsState.getCurrentOrganisation));
  public currentOrganisation = signal<IOrganisationListItem | undefined>(undefined);
  public hostname: string;
  public interval: ReturnType<typeof setInterval>;
  public leMdMedia = signal(false);
  public xsMedia = signal(false);
  public lgMedia = signal(false);
  public xlMedia = signal(false);
  public hasNonDemoOrg = signal(false);
  public notifications = signal<INotification[]>([] as INotification[]);
  public notificationsLoading = signal(false);
  public newNotificationsCount$$ = signal<number | undefined>(undefined);
  public dataActLink$$ = this.initializeDataActLink();
  public legalTextLinks$$: Signal<LegalTextLink[]> = signal([]);
  public sapIsEnabled$$ = this.initializeSapToggle();

  // Protected properties
  protected eventTrackingService = inject(EventTrackingService);

  // Private properties
  private router = inject(Router);
  private change = inject(ChangeDetectorRef);
  private platformId = inject<number | string>(PLATFORM_ID);
  private notificationsService = inject(NotificationsService);
  private subscription = new Subscription();
  private initRegistrationDefaultOrganisation = true;
  private currentLang = toSignal<LangChangeEvent, LangChangeEvent>(this.translateService.onLangChange, {
    initialValue: { lang: this.translateService.getCurrentLang(), translations: {} as InterpolatableTranslationObject },
  });
  private currentNormalizedLang = computed(() => this.initNormalizedLanguage());

  constructor() {
    super();

    effect(() => {
      if (this.currentOrg()) {
        this.refreshNotifications();
      }
    });
  }

  public ngAfterViewInit() {
    this.cdRef.detectChanges();
  }

  public ngOnInit() {
    this.hostname = location.hostname;

    this.subscription.add(
      this.user$.subscribe((user: User | null) => {
        this.currentUser.set(user as ExtendedUser);
        if (!user) {
          return;
        }

        this.showNavbar.set(!!user?.uuid);
        this.repeatOrgSelectorRefresh();

        // default org can be retrieved only when the user country is present.
        // navbar component needs registration default organisation data to handle
        // data for current demo org as well as from currently selected org
        this.store.dispatch(new OrganisationActions.GetRegistrationDefaultOrganisation(user?.user_metadata.country));
      })
    );

    // creates watcher on store for organizations
    this.subscription.add(
      this.lastSelectedOrganisation$.subscribe((currOrg: IOrganisationListItem | undefined) => {
        if (!currOrg) {
          return;
        }

        if (this.initRegistrationDefaultOrganisation) {
          this.store.dispatch(
            new OrganisationActions.GetRegistrationDefaultOrganisation(
              this.currentUser()?.user_metadata?.country ?? 'de'
            )
          );
          this.initRegistrationDefaultOrganisation = false;
        }
        this.notifications.set([]);
        this.notificationsLoading.set(true);
        this.currentOrganisation.set(currOrg);
      })
    );

    this.subscription.add(
      this.store.select(getOrganisationChanging).subscribe(orgChanging => {
        if (orgChanging) {
          this.notifications.set([]);
        }
      })
    );

    this.subscription.add(
      combineLatest([this.allOrgNotificationCount$, this.organisations$]).subscribe(
        ([allOrgNotificationCount, organisations]) => {
          this.allOrgNotificationCount.set(allOrgNotificationCount);
          const registeredOrgs = organisations.filter(org => !!org.registered);
          this.organisations.set(
            this.updatedOrganisationsWithNotificationsCount(registeredOrgs, this.allOrgNotificationCount())
          );
          this.newNotificationsCount$$.set(this.getNotificationCountForOrg(this.currentOrganisation()));
        }
      )
    );

    this.subscription.add(
      this.breakpointObserver
        .observe([Breakpoints.Large, Breakpoints.XLarge, Breakpoints.Medium, Breakpoints.Small, Breakpoints.XSmall])
        .subscribe((state: BreakpointState) => {
          this.leMdMedia.set(
            state.breakpoints[Breakpoints.XSmall] ||
              state.breakpoints[Breakpoints.Small] ||
              state.breakpoints[Breakpoints.Medium]
          );
          this.xsMedia.set(state.breakpoints[Breakpoints.XSmall]);
          this.lgMedia.set(state.breakpoints[Breakpoints.Large]);
          this.xlMedia.set(state.breakpoints[Breakpoints.XLarge]);

          this.cdRef.detectChanges();
        })
    );

    this.legalTextLinks$$ = computed(() => {
      let language = this.currentLang()?.lang ?? 'en';
      if (language === 'en-us' || language === 'en-gb') {
        language = 'en';
      }

      return [
        {
          label: 'navbar.privacy-policy',
          link: `${environment.helpCenterBaseUrl}/${language}/data-protection`,
        },
        {
          label: 'navbar.terms-of-use',
          link: `${environment.helpCenterBaseUrl}/${language}/legal-terms`,
        },
        {
          label: 'navbar.impressum',
          link: `${environment.helpCenterBaseUrl}/${language}/impressum`,
        },
      ];
    });

    this.initializeSapToggle();
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();

    clearInterval(this.interval);

    super.ngOnDestroy();
  }

  public setCurrentOrganisation(org: IOrganisationListItem | null): void {
    this.eventTrackingService.eventTrackNew({
      payload: {
        event: 'machinemgtevent',
        eventCategory: 'Machine Management Header',
        eventAction: 'Click Button',
        eventName: 'Select organization',
      },
    });
    if (org === null) {
      if (isPlatformBrowser(this.platformId)) {
        window.open(environment.claasIdDashboardBaseUrl + '/personal-data', '_blank');
      }
      return;
    }
    const country = org.country ?? '';
    this.setCurrentOrganisationId(org.auth0Id, country);
  }

  public registerOrganisation(user: User | null, dialogSteps: IRegistrationDialogStep[] = []): void {
    const dialogInput: Partial<IOrganisationRegistrationDialogInput> = {
      user: user as User,
      dialogSteps,
    };

    this.eventTrackingService.eventTrackNew({
      payload: {
        event: 'machinemgtevent',
        eventCategory: 'Machine Management Header',
        eventAction: 'Click Button',
        eventName: 'Create Organization',
      },
    });

    this.openDialog(dialogInput).pipe(take(1)).subscribe();
  }

  public gotoClaasIdInviteUser(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.eventTrackingService.eventTrackNew({
        payload: {
          event: 'machinemgtevent',
          eventCategory: 'Machine Management Header',
          eventAction: 'Click Button',
          eventName: 'Invite user',
        },
      });

      window.open(
        `${environment.claasIdDashboardBaseUrl}/organisation-data?orgId=${this.currentOrganisation()?.auth0Id}&selectedTab=members&startInvite=true`,
        '_blank'
      );
    }
  }

  public setLang(lang: string): void {
    this.translateService.use(lang);
  }

  public getAllNotificationsCount() {
    return this.allOrgNotificationCount().reduce((n, { total }) => n + total, 0);
  }

  public getNotificationCountForOrg(org: IOrganisationListItem | undefined) {
    const orgNotificationCount = this.allOrgNotificationCount().find(o => o.organizationId === org?.id);
    return orgNotificationCount ? orgNotificationCount.total : 0;
  }

  public onLogout(): void {
    this.eventTrackingService.eventTrackNew({
      payload: {
        event: 'machinemgtevent',
        eventCategory: 'Machine Management Header',
        eventAction: 'Click Button',
        eventName: 'Logout from System',
      },
    });
    this.store.dispatch(new AuthActions.Logout());
    this.change.detectChanges();
  }

  public onClaasIdSettings(suffix: string): void {
    this.eventTrackingService.eventTrackNew({
      payload: {
        event: 'machinemgtevent',
        eventCategory: 'Machine Management Header',
        eventAction: 'Click Button',
        eventName: 'Select settings',
        customDimensions: { [CustomDimension.EVENT_LABEL]: suffix },
      },
    });
    if (isPlatformBrowser(this.platformId)) {
      window.open(`${environment.claasIdDashboardBaseUrl}/${suffix}`, '_blank');
    }
  }

  public trackOpenOrgSelector(): void {
    this.eventTrackingService.eventTrackNew({
      payload: {
        event: 'machinemgtevent',
        eventCategory: 'Machine Management Header',
        eventAction: 'Click Dropdown',
        eventName: 'Open organization selector',
      },
    });
  }

  public trackOpenAppSelector(): void {
    this.eventTrackingService.eventTrackNew({
      payload: {
        event: 'machinemgtevent',
        eventCategory: 'Machine Management Header',
        eventAction: 'Click Button',
        eventName: 'Open app selector',
      },
    });
  }

  public trackOpenAccountMenu(): void {
    this.eventTrackingService.eventTrackNew({
      payload: {
        event: 'machinemgtevent',
        eventCategory: 'Machine Management Header',
        eventAction: 'Click Button',
        eventName: 'Open account menu',
      },
    });
  }

  public refreshNotifications() {
    this.getNotifications();
    this.requestNotificationUpdate();
  }

  private repeatOrgSelectorRefresh(): void {
    // 01.10.24 deicision: remove interval to periodically update the
    // organisation list for now because it is not neccessary anymore to detect
    // if an user was removed from an org. Leave the flag to keep the logic in
    // the organisation effects
    // not needed here at all because rehydrate action also calls the org list
    // this.store.dispatch(new OrganisationActions.List({ periodically: true })); // org -> notif -> per user count -> couplings
    // old interval behaviour:
    // this.interval = setInterval(() => {
    //   // Refresh orgs periodically too!
    //   // If an user will be kicked out of an organization the organization selector has to be refreshed
    //   this.store.dispatch(new OrganisationActions.List({ periodically: true })); // org -> notif -> per user count -> couplings
    //   // this.requestNotificationUpdate();
    // }, 30 * 1000);
  }

  private handleNavigation(): void {
    const routeUrl = this.router.url;
    // list of routes that are allowed to be navigated to after a org change.
    // more specific routes are listed first.
    const routes = [
      '/analytics/reports',
      'analytics/comparison',
      '/analytics',
      '/configurations',
      '/connections',
      '/agin-connections',
      '/couplings',
      '/machines/machine-sharing',
      '/machines',
      '/privacy/dataPrivacy',
      '/privacy/termsOfUse',
      '/privacy/imprint',
      '/privacy',
      '/shops',
      '/parts-shop',
      '/support',
      '/license',
      '/file-transfer',
    ];

    const navigationRoute = routes.find(route => routeUrl.includes(route));

    if (navigationRoute) {
      void this.router.navigate([navigationRoute]);
    } else {
      void this.router.navigate(['/']);
    }
  }

  private requestNotificationUpdate(): void {
    this.store.dispatch(new OrganisationActions.GetNotificationCount());
  }

  private setCurrentOrganisationId(orgId: string, country: string) {
    if (!this.currentOrganisation() || this.currentOrganisation()?.auth0Id !== orgId) {
      // RESET CONFIGURATION IN STORE ON COMPONENT LOAD
      // this.store.dispatch(new ConfigurationsActions.ConfigurationsSuccess(null));
      // this.store.dispatch(new ConfigurationsActions.HandleConfigurationLoader(true));
      if (isPlatformBrowser(this.platformId)) {
        this.store.dispatch(new OrganisationActions.SetCurrentOrganisation({ auth0Id: orgId }));
        this.store.dispatch(new OrganisationActions.GetRegistrationDefaultOrganisation(country));
      }

      this.handleNavigation(); // handles wheter route will be changed to dashboard or not, after org change
    }
  }

  private getNotifications() {
    this.notificationsLoading.set(true);
    this.notificationsService
      .getNotifications({ page: 0, size: 20, language: this.translateService.getCurrentLang() })
      .pipe(take(1))
      .subscribe(
        paginatedNotifications => {
          this.notifications.set(paginatedNotifications.content);
          this.notificationsLoading.set(false);
        },
        () => {
          this.notificationsLoading.set(false);
        }
      );
  }

  private updatedOrganisationsWithNotificationsCount(
    organisations: IOrganisationListItem[],
    allOrgNotificationCount: IOrganisationNotificationCountItem[]
  ): IOrganisationListItem[] {
    return organisations.map(organisation => {
      const notificationItem = allOrgNotificationCount.find(
        orgNotificationCount => orgNotificationCount.organizationId === organisation.id
      );
      return {
        ...organisation,
        notificationCount: notificationItem ? notificationItem.total : organisation.notificationCount,
      };
    });
  }

  private initNormalizedLanguage() {
    let language = this.currentLang()?.lang ?? 'en';
    if (language === 'en-us' || language === 'en-gb') {
      language = 'en';
    }
    return language.toLowerCase();
  }

  private initializeSapToggle() {
    return computed(() => {
      const featureToggles = this.featureToggleStore.featureToggles();
      return featureToggles.some(toggle => Features.SAP === toggle.featureId);
    });
  }

  private initializeDataActLink() {
    return computed(() => {
      // link changes based on language, when language does not exist in help center it will fallback to english
      const language = this.currentNormalizedLang();
      return `${environment.helpCenterBaseUrl}/${language}/data-act`;
    });
  }
}
